import type { QueryClient } from '@tanstack/react-query';

import {
    getLensScenarioConfigQueryKey,
    PPA_LENS_SCENARIO_CONFIG_QUERY_KEY,
} from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLensConfigCache.helpers';
import { buildScenarioViewSeedFromLiveConfig } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.helpers';
import {
    isPpaScenarioViewMetadataDegraded,
    isPpaScenarioViewMetadataUnusable,
} from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLensMetadata.helpers';
import type {
    LiveViewConfig,
    ScenarioViewData,
} from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.types';
import { fetchLensScenarioConfig } from 'hooks/ppa/useFetchLensScenarioConfig';
import { fetchScenarioViewMetadata, getPpaViewMetadataQueryKey } from 'hooks/ppa/useFetchViewMetadata';
import { fetchPpaViewList, getPpaViewListQueryKey, switchPpaScenarioView } from 'hooks/ppa/usePpaViews';
import type { PPAGetViewListResponse, PPAScopeFilter, PPAViewDataSource, PPAViewListItem } from 'types/ppaViews';
import { getAuthUserMail } from 'utils/helperMethods';

export { getPpaViewMetadataQueryKey } from 'hooks/ppa/useFetchViewMetadata';

export type SelectedViewFromList = {
    viewId: number | null;
    dataSource: PPAViewDataSource;
};

export const resolveFallbackSavedView = (
    savedViews: PPAViewListItem[],
    preferredViewId?: number | null,
): PPAViewListItem | null =>
    savedViews.find((view) => view.is_default) ??
    (preferredViewId != null
        ? savedViews.find((view) => view.view_id === preferredViewId)
        : undefined) ??
    savedViews[0] ??
    null;

/** When scenario deviation metadata is empty, load the base saved view instead. */
export async function resolveWorkspaceViewSelection(
    queryClient: QueryClient,
    params: {
        scenarioId: number;
        viewListData: PPAGetViewListResponse;
        userId?: string | null;
        isScenarioOwner?: boolean;
    },
): Promise<SelectedViewFromList> {
    const resolvedUserId = (params.userId ?? getAuthUserMail()) || null;
    let selection = resolveSelectedViewFromList(params.viewListData);

    if (selection.viewId == null || selection.dataSource !== 'scenario') {
        return selection;
    }

    const scenarioMetadata = await queryClient.fetchQuery({
        queryKey: getPpaViewMetadataQueryKey(
            selection.viewId,
            params.scenarioId,
            'scenario',
            resolvedUserId,
        ),
        queryFn: () =>
            fetchScenarioViewMetadata(selection.viewId as number, {
                scenarioId: params.scenarioId,
                dataSource: 'scenario',
            }),
        staleTime: 0,
    });

    if (!isPpaScenarioViewMetadataUnusable(scenarioMetadata)) {
        const baseViewMetadata = await queryClient.fetchQuery({
            queryKey: getPpaViewMetadataQueryKey(
                selection.viewId as number,
                params.scenarioId,
                'view',
                resolvedUserId,
            ),
            queryFn: () =>
                fetchScenarioViewMetadata(selection.viewId as number, {
                    scenarioId: params.scenarioId,
                    dataSource: 'view',
                }),
            staleTime: 0,
        });

        if (!isPpaScenarioViewMetadataDegraded(scenarioMetadata, baseViewMetadata)) {
            return selection;
        }

        if (params.isScenarioOwner) {
            await activateScenarioViewSwitch(queryClient, {
                scenarioId: params.scenarioId,
                viewId: selection.viewId as number,
                isScenarioOwner: true,
                userId: resolvedUserId,
            });
        } else {
            await queryClient.fetchQuery({
                queryKey: getPpaViewMetadataQueryKey(
                    selection.viewId as number,
                    params.scenarioId,
                    'view',
                    resolvedUserId,
                ),
                queryFn: () =>
                    fetchScenarioViewMetadata(selection.viewId as number, {
                        scenarioId: params.scenarioId,
                        dataSource: 'view',
                    }),
                staleTime: 0,
            });
        }

        return { viewId: selection.viewId, dataSource: 'view' };
    }

    const savedViews = params.viewListData.views.filter((view) => view.data_source !== 'scenario');
    const fallbackView = resolveFallbackSavedView(savedViews, selection.viewId);
    if (!fallbackView) {
        return selection;
    }

    if (params.isScenarioOwner) {
        await activateScenarioViewSwitch(queryClient, {
            scenarioId: params.scenarioId,
            viewId: fallbackView.view_id,
            isScenarioOwner: true,
            userId: resolvedUserId,
        });
    } else {
        await queryClient.fetchQuery({
            queryKey: getPpaViewMetadataQueryKey(
                fallbackView.view_id,
                params.scenarioId,
                'view',
                resolvedUserId,
            ),
            queryFn: () =>
                fetchScenarioViewMetadata(fallbackView.view_id, {
                    scenarioId: params.scenarioId,
                    dataSource: 'view',
                }),
            staleTime: 0,
        });
    }

    selection = { viewId: fallbackView.view_id, dataSource: 'view' };
    return selection;
}

export const resolveSelectedViewFromList = (viewListData: PPAGetViewListResponse): SelectedViewFromList => {
    const selectedView = viewListData.views.find((view) => view.is_selected);
    if (selectedView) {
        return {
            viewId: selectedView.view_id,
            dataSource: selectedView.data_source ?? 'view',
        };
    }

    if (viewListData.selected_view_id != null) {
        return {
            viewId: viewListData.selected_view_id,
            dataSource: viewListData.selected_data_source ?? 'view',
        };
    }

    const defaultView = viewListData.views.find(
        (view) => view.is_default && view.data_source !== 'scenario',
    );
    const firstSavedView = viewListData.views.find((view) => view.data_source !== 'scenario');
    return {
        viewId: defaultView?.view_id ?? firstSavedView?.view_id ?? null,
        dataSource: 'view',
    };
};

/** @deprecated Use resolveSelectedViewFromList */
export const resolveViewIdFromListResponse = (viewListData: PPAGetViewListResponse): number | null =>
    resolveSelectedViewFromList(viewListData).viewId;

export const getActiveLensIdFromViewMetadata = (metadata: ScenarioViewData): number | null => {
    const activeLens = metadata.lenses.find((lens) => lens.is_active_lens);
    if (activeLens) {
        return activeLens.lens_id;
    }

    const firstSelectedLens = metadata.lenses.find((lens) => lens.is_selected);
    return firstSelectedLens?.lens_id ?? null;
};

const preserveViewListTimestamps = (
    previous: PPAGetViewListResponse | undefined,
    next: PPAGetViewListResponse,
): PPAGetViewListResponse => {
    if (!previous) {
        return next;
    }

    const timestampByViewId = new Map(
        previous.views.map((view) => [
            view.view_id,
            { created_at: view.created_at, updated_at: view.updated_at },
        ]),
    );

    return {
        ...next,
        views: next.views.map((view) => {
            const cached = timestampByViewId.get(view.view_id);
            if (!cached) {
                return view;
            }

            return {
                ...view,
                created_at: view.created_at ?? cached.created_at,
                updated_at: view.updated_at ?? cached.updated_at,
            };
        }),
    };
};

export async function prefetchPpaViewList(
    queryClient: QueryClient,
    scenarioId: number,
    userId?: string | null,
    ppaScopeId?: number | null,
): Promise<PPAGetViewListResponse> {
    const resolvedUserId = (userId ?? getAuthUserMail()) || null;
    const queryKey = getPpaViewListQueryKey(scenarioId, resolvedUserId, ppaScopeId);
    const previous = queryClient.getQueryData<PPAGetViewListResponse>(queryKey);
    const next = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => fetchPpaViewList(scenarioId),
    });
    const merged = preserveViewListTimestamps(previous, next);
    queryClient.setQueryData(queryKey, merged);
    return merged;
}

/** Drop cached view list, metadata, and lens-scenario config for a scenario workspace. */
export function clearScenarioWorkspaceCache(
    queryClient: QueryClient,
    scenarioId: number | null | undefined,
    userId?: string | null,
    options?: { includeViewList?: boolean; ppaScopeId?: number | null },
): void {
    const resolvedUserId = (userId ?? getAuthUserMail()) || null;
    const includeViewList = options?.includeViewList !== false;
    const ppaScopeId = options?.ppaScopeId ?? null;

    if (includeViewList && scenarioId != null) {
        queryClient.removeQueries({
            queryKey: getPpaViewListQueryKey(scenarioId, resolvedUserId, ppaScopeId),
        });
    }

    queryClient.removeQueries({
        predicate: (query) => {
            const key = query.queryKey;
            if (!Array.isArray(key)) {
                return false;
            }
            if (key[0] === 'ppa-view-metadata' && key[2] === scenarioId) {
                return true;
            }
            if (key[0] === PPA_LENS_SCENARIO_CONFIG_QUERY_KEY && key[2] === scenarioId) {
                return true;
            }
            return false;
        },
    });
}

/** Invalidate caches after custom lens delete so scenario/base view state stays aligned. */
export function invalidateLensDeleteWorkspaceCache(
    queryClient: QueryClient,
    params: {
        scenarioId?: number | null;
        viewId?: number | null;
        userId?: string | null;
        ppaScopeId?: number | null;
    },
): void {
    const resolvedUserId = (params.userId ?? getAuthUserMail()) || null;

    if (params.scenarioId != null) {
        void queryClient.invalidateQueries({
            queryKey: getPpaViewListQueryKey(params.scenarioId, resolvedUserId, params.ppaScopeId),
        });
    }

    if (params.viewId == null) {
        return;
    }

    for (const dataSource of ['view', 'scenario'] as const) {
        queryClient.removeQueries({
            queryKey: getPpaViewMetadataQueryKey(
                params.viewId,
                params.scenarioId,
                dataSource,
                resolvedUserId,
            ),
        });
    }
}

/** Always hit network: get_view_list, then get_view_metadata for the selected view. */
export async function bootstrapScenarioWorkspaceFresh(
    queryClient: QueryClient,
    scenarioId: number,
    userId?: string | null,
    ppaScopeId?: number | null,
): Promise<SelectedViewFromList> {
    const resolvedUserId = (userId ?? getAuthUserMail()) || null;

    clearScenarioWorkspaceCache(queryClient, scenarioId, resolvedUserId, { ppaScopeId });

    const viewListData = await queryClient.fetchQuery({
        queryKey: getPpaViewListQueryKey(scenarioId, resolvedUserId, ppaScopeId),
        queryFn: () => fetchPpaViewList(scenarioId),
        staleTime: 0,
    });

    const selection = resolveSelectedViewFromList(viewListData);

    if (selection.viewId != null) {
        await queryClient.fetchQuery({
            queryKey: getPpaViewMetadataQueryKey(
                selection.viewId,
                scenarioId,
                selection.dataSource,
                resolvedUserId,
            ),
            queryFn: () =>
                fetchScenarioViewMetadata(selection.viewId as number, {
                    scenarioId,
                    dataSource: selection.dataSource,
                }),
            staleTime: 0,
        });
    }

    return selection;
}

/** Mark the scenario deviation entry as selected in the cached view list (no network). */
export function applyOptimisticScenarioViewSelection(
    queryClient: QueryClient,
    scenarioId: number,
    baseViewId: number,
    userId?: string | null,
    ppaScopeId?: number | null,
): void {
    const resolvedUserId = (userId ?? getAuthUserMail()) || null;
    queryClient.setQueryData<PPAGetViewListResponse>(
        getPpaViewListQueryKey(scenarioId, resolvedUserId, ppaScopeId),
        (current) => {
            if (!current) {
                return current;
            }

            const savedViews = current.views.filter((item) => item.data_source !== 'scenario');
            const scenarioItem: PPAViewListItem = {
                view_id: baseViewId,
                view_name: 'Scenario View',
                view_type: 'SCENARIO',
                created_by: resolvedUserId,
                created_at: null,
                updated_at: null,
                is_default: false,
                is_selected: true,
                data_source: 'scenario',
            };

            return {
                ...current,
                views: [
                    scenarioItem,
                    ...savedViews.map((item) => ({
                        ...item,
                        is_selected: false,
                    })),
                ],
                selected_view_id: baseViewId,
                selected_data_source: 'scenario',
                selection_source: 'scenario_view',
            };
        },
    );
}

export type SeedScenarioViewMetadataParams = {
    scenarioId: number;
    userId?: string | null;
    baseViewId: number;
    liveConfig: LiveViewConfig;
    sourceMetadata?: ScenarioViewData;
};

/** Seed scenario metadata from the live workspace config after exit-view snapshot save. */
export function seedScenarioViewMetadataCache(
    queryClient: QueryClient,
    params: SeedScenarioViewMetadataParams,
): boolean {
    const resolvedUserId = (params.userId ?? getAuthUserMail()) || null;
    let source = params.sourceMetadata;

    if (!source) {
        source = queryClient.getQueryData<ScenarioViewData>(
            getPpaViewMetadataQueryKey(params.baseViewId, params.scenarioId, 'view', resolvedUserId),
        );
    }

    if (!source) {
        return false;
    }

    const seeded = buildScenarioViewSeedFromLiveConfig(source, params.liveConfig, {
        viewId: params.baseViewId,
        viewName: 'Scenario View',
        isDefault: false,
    });

    queryClient.setQueryData(
        getPpaViewMetadataQueryKey(params.baseViewId, params.scenarioId, 'scenario', resolvedUserId),
        seeded,
    );
    return true;
}

export function clearOptimisticViewListSelection(
    queryClient: QueryClient,
    scenarioId: number,
    userId?: string | null,
    ppaScopeId?: number | null,
): void {
    const resolvedUserId = (userId ?? getAuthUserMail()) || null;
    queryClient.setQueryData<PPAGetViewListResponse>(
        getPpaViewListQueryKey(scenarioId, resolvedUserId, ppaScopeId),
        (current) => {
            if (!current) {
                return current;
            }

            return {
                ...current,
                selected_view_id: null,
                selected_data_source: undefined,
                views: current.views.map((item) => ({
                    ...item,
                    is_selected: false,
                })),
            };
        },
    );
}

export type CreatedViewListCacheInput = {
    view_id: number;
    view_name: string;
    is_default: boolean;
    created_by?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
};

/** Append a newly created view to the cached list and mark it selected (no network). */
export function appendCreatedViewToViewListCache(
    queryClient: QueryClient,
    scenarioId: number | null | undefined,
    userId: string | null | undefined,
    created: CreatedViewListCacheInput,
    ppaScopeId?: number | null,
): void {
    if (scenarioId == null) {
        return;
    }

    const resolvedUserId = (userId ?? getAuthUserMail()) || null;
    queryClient.setQueryData<PPAGetViewListResponse>(
        getPpaViewListQueryKey(scenarioId, resolvedUserId, ppaScopeId),
        (current) => {
            if (!current) {
                return current;
            }

            const savedAt = created.updated_at ?? created.created_at ?? new Date().toISOString();
            const newItem: PPAViewListItem = {
                view_id: created.view_id,
                view_name: created.view_name,
                view_type: 'CUSTOM',
                created_by: created.created_by ?? resolvedUserId,
                created_at: created.created_at ?? savedAt,
                updated_at: created.updated_at ?? savedAt,
                is_default: created.is_default,
                is_selected: true,
                data_source: 'view',
            };

            const views = [
                ...current.views.map((item) => ({
                    ...item,
                    is_selected: false,
                    is_default: created.is_default && item.data_source !== 'scenario' ? false : item.is_default,
                })),
                newItem,
            ];

            return {
                ...current,
                views,
                total_count: current.total_count + 1,
                selected_view_id: created.view_id,
                selected_data_source: 'view',
                selection_source: 'scenario_view',
            };
        },
    );
}

export function applyOptimisticViewListSelection(
    queryClient: QueryClient,
    scenarioId: number,
    viewId: number,
    userId?: string | null,
    ppaScopeId?: number | null,
): void {
    const resolvedUserId = (userId ?? getAuthUserMail()) || null;
    queryClient.setQueryData<PPAGetViewListResponse>(
        getPpaViewListQueryKey(scenarioId, resolvedUserId, ppaScopeId),
        (current) => {
            if (!current) {
                return current;
            }

            return {
                ...current,
                selected_view_id: viewId,
                selected_data_source: 'view',
                selection_source: 'scenario_view',
                views: current.views
                    .filter((item) => item.data_source !== 'scenario')
                    .map((item) => ({
                        ...item,
                        is_selected: item.view_id === viewId,
                    })),
            };
        },
    );
}

/** Mark a view as default in the cached list immediately. Returns the prior cache for rollback. */
export function applyOptimisticDefaultView(
    queryClient: QueryClient,
    scenarioId: number,
    viewId: number,
    userId?: string | null,
    ppaScopeId?: number | null,
): PPAGetViewListResponse | undefined {
    const resolvedUserId = (userId ?? getAuthUserMail()) || null;
    const queryKey = getPpaViewListQueryKey(scenarioId, resolvedUserId, ppaScopeId);
    const previous = queryClient.getQueryData<PPAGetViewListResponse>(queryKey);

    queryClient.setQueryData<PPAGetViewListResponse>(queryKey, (current) => {
        if (!current) {
            return current;
        }

        return {
            ...current,
            views: current.views.map((item) => ({
                ...item,
                is_default:
                    item.data_source === 'scenario'
                        ? item.is_default
                        : item.view_id === viewId,
            })),
        };
    });

    return previous;
}

export function restoreViewListCache(
    queryClient: QueryClient,
    scenarioId: number,
    snapshot: PPAGetViewListResponse | undefined,
    userId?: string | null,
    ppaScopeId?: number | null,
): void {
    if (!snapshot) {
        return;
    }

    const resolvedUserId = (userId ?? getAuthUserMail()) || null;
    queryClient.setQueryData(
        getPpaViewListQueryKey(scenarioId, resolvedUserId, ppaScopeId),
        snapshot,
    );
}

export type SeedCreatedViewMetadataParams = {
    scenarioId: number;
    userId?: string | null;
    createdViewId: number;
    createdViewName: string;
    isDefault: boolean;
    sourceViewId?: number;
    sourceMetadata?: ScenarioViewData;
    filters?: PPAScopeFilter[];
};

/** Seed metadata for a newly created view from the current workspace (avoids get_view_metadata on save).
 *  Caller should pass sourceMetadata derived from the same live config used for the create payload. */
export function seedCreatedViewMetadataCache(
    queryClient: QueryClient,
    params: SeedCreatedViewMetadataParams,
): boolean {
    const resolvedUserId = (params.userId ?? getAuthUserMail()) || null;
    let source = params.sourceMetadata;

    if (!source && params.sourceViewId != null) {
        source = queryClient.getQueryData<ScenarioViewData>(
            getPpaViewMetadataQueryKey(params.sourceViewId, params.scenarioId, 'view', resolvedUserId),
        );
    }

    if (!source) {
        return false;
    }

    const seeded: ScenarioViewData = {
        ...source,
        view_id: params.createdViewId,
        view_name: params.createdViewName,
        is_default: params.isDefault,
        updated_at: null,
        scope: params.filters ?? source.scope,
        pending_messages: [],
    };

    queryClient.setQueryData(
        getPpaViewMetadataQueryKey(params.createdViewId, params.scenarioId, 'view', resolvedUserId),
        seeded,
    );
    return true;
}

/** Owner: switch_scenario_view + get_view_metadata. Reader: metadata only. */
export async function activateScenarioViewSwitch(
    queryClient: QueryClient,
    params: {
        scenarioId: number;
        viewId: number;
        isScenarioOwner: boolean;
        userId?: string | null;
        /** Skip metadata network call when cache was seeded (e.g. after Save Current View). */
        preferCachedMetadata?: boolean;
        prefetchLensConfig?: boolean;
        ppaScopeId?: number | null;
    },
): Promise<ScenarioViewData> {
    const resolvedUserId = (params.userId ?? getAuthUserMail()) || null;
    const metadataKey = getPpaViewMetadataQueryKey(
        params.viewId,
        params.scenarioId,
        'view',
        resolvedUserId,
    );

    const switchPromise = params.isScenarioOwner
        ? switchPpaScenarioView(params.scenarioId, params.viewId).then(() => {
              applyOptimisticViewListSelection(
                  queryClient,
                  params.scenarioId,
                  params.viewId,
                  resolvedUserId,
                  params.ppaScopeId,
              );
          })
        : Promise.resolve();

    const cachedMetadata =
        params.preferCachedMetadata === true
            ? queryClient.getQueryData<ScenarioViewData>(metadataKey)
            : undefined;

    const metadataPromise =
        cachedMetadata != null
            ? Promise.resolve(cachedMetadata)
            : queryClient.fetchQuery({
                  queryKey: metadataKey,
                  queryFn: () =>
                      fetchScenarioViewMetadata(params.viewId, {
                          scenarioId: params.scenarioId,
                          dataSource: 'view',
                      }),
              });

    const [, metadata] = await Promise.all([switchPromise, metadataPromise]);

    if (params.prefetchLensConfig !== false) {
        const activeLensId = getActiveLensIdFromViewMetadata(metadata);
        if (activeLensId != null) {
            void queryClient.prefetchQuery({
                queryKey: getLensScenarioConfigQueryKey(activeLensId, params.scenarioId),
                queryFn: () => fetchLensScenarioConfig(activeLensId, params.scenarioId),
            });
        }
    }

    return metadata;
}

export async function prefetchScenarioViewChain(
    queryClient: QueryClient,
    params: {
        scenarioId: number;
        viewId: number;
        dataSource?: PPAViewDataSource;
        userId?: string | null;
    },
): Promise<void> {
    const resolvedUserId = (params.userId ?? getAuthUserMail()) || null;
    const dataSource = params.dataSource ?? 'view';
    const metadata = await queryClient.fetchQuery({
        queryKey: getPpaViewMetadataQueryKey(params.viewId, params.scenarioId, dataSource, resolvedUserId),
        queryFn: () =>
            fetchScenarioViewMetadata(params.viewId, {
                scenarioId: params.scenarioId,
                dataSource,
            }),
    });

    const activeLensId = getActiveLensIdFromViewMetadata(metadata);
    if (activeLensId == null) {
        return;
    }

    await queryClient.prefetchQuery({
        queryKey: getLensScenarioConfigQueryKey(activeLensId, params.scenarioId),
        queryFn: () => fetchLensScenarioConfig(activeLensId, params.scenarioId),
    });
}

export async function prefetchScenarioWorkspace(
    queryClient: QueryClient,
    scenarioId: number,
    userId?: string | null,
    ppaScopeId?: number | null,
): Promise<void> {
    const viewListData = await prefetchPpaViewList(queryClient, scenarioId, userId, ppaScopeId);
    const selection = resolveSelectedViewFromList(viewListData);
    if (selection.viewId == null) {
        return;
    }

    await prefetchScenarioViewChain(queryClient, {
        scenarioId,
        viewId: selection.viewId,
        dataSource: selection.dataSource,
        userId,
    });
}
